import { styled } from '@mui/system';
import OpenHours from './components/OpenHours';
import CafeInfo from './components/CafeInfo';
import TimeZoneCreateBtn from './components/TimeZoneCreateBtn';
import { useEditCafeMutation, useGetCafeProfileQuery } from '../../store/api/cafeApi';
import { useFormik } from 'formik';
import { cafeSchema } from '../../schemas/cafe.request';
import ImageUpload from './components/ImageUpload';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Navigate, generatePath, useLocation } from 'react-router-dom';
import { extractIdFromURL } from '../../utils/common';
import Loading from '../../components/ui/Loading';
import { toast } from 'react-toastify';
import { useEffect } from 'react';

const EditContainer = styled('div')(({ theme }) => ({
  maxWidth: '827px',
  minHeight: '730px',
  border: '1px solid #ffff',
  background:
    'linear-gradient(250.42deg, rgba(255, 255, 255, 0.32) 0%, rgba(255, 255, 255, 0.08) 101.65%)',
  padding: '16px 33px 24px 25px',
  margin: '50px auto 25px',
  [theme.breakpoints.down('md')]: {
    padding: '16px 25px 24px 25px',
  },
  [theme.breakpoints.down('sm')]: {
    border: 'none',
    background: 'none',
    padding: '0',
  },
}));

const CafeCreateForm = styled('form')(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const ContentEditContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '386px',
  padding: '0 0 0 36px',
  [theme.breakpoints.down('md')]: {
    width: '350px',
    padding: '0',
    margin: '0 auto',
  },
}));

const openHoursData = [
  { day: 'Monday', openTime: '', closeTime: '', isClosed: false },
  { day: 'Tuesday', openTime: '', closeTime: '', isClosed: false },
  { day: 'Wednesday', openTime: '', closeTime: '', isClosed: false },
  { day: 'Thursday', openTime: '', closeTime: '', isClosed: false },
  { day: 'Friday', openTime: '', closeTime: '', isClosed: false },
  { day: 'Saturday', openTime: '', closeTime: '', isClosed: false },
  { day: 'Sunday', openTime: '', closeTime: '', isClosed: false },
];

const validate = (values) => {
  const errors = {};
  const valid = cafeSchema.validate(values, { abortEarly: false });
  if (valid.error) {
    valid.error.details.forEach((err) => {
      errors[err.path.join('.')] = err.message;
    });
  }
  return errors;
};

const CafeEditPage = (props) => {
  const mobileMatch = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const location = useLocation();
  const currentCafeId = extractIdFromURL(location.pathname);
  const [editCafe, { data: editData, isSuccess, isLoading }] = useEditCafeMutation();
  const { data: cafe } = useGetCafeProfileQuery(currentCafeId);
  const currentCafeData = cafe.data;

  const formik = useFormik({
    initialValues: {
      timeZone: currentCafeData.timeZone,
      cafeName: currentCafeData.cafeName,
      cafePhone: currentCafeData.cafePhone,
      attachmentData: [
        {
          url: '',
          name: '',
        },
      ],
      streetNumber: currentCafeData.cafeAddress.streetNumber,
      streetName: currentCafeData.cafeAddress.streetName,
      state: currentCafeData.cafeAddress.state,
      city: currentCafeData.cafeAddress.city,
      country: 'Australia',
      postcode: currentCafeData.cafeAddress.postcode,
      isDeleted: false,
      Mon: currentCafeData.tradingHours.Mon,
      Tue: {
        day: 'Tuesday',
        openTime: '',
        closeTime: '',
        isClosed: false,
      },
      Wed: {
        day: 'Wednesday',
        openTime: '',
        closeTime: '',
        isClosed: false,
      },
      Thu: {
        day: 'Thursday',
        openTime: '',
        closeTime: '',
        isClosed: false,
      },
      Fri: {
        day: 'Friday',
        openTime: '',
        closeTime: '',
        isClosed: false,
      },
      Sat: {
        day: 'Saturday',
        openTime: '',
        closeTime: '',
        isClosed: false,
      },
      Sun: {
        day: 'Sunday',
        openTime: '',
        closeTime: '',
        isClosed: false,
      },
    },
    validate,
    onSubmit: async (values) => {
      values.Mon = openHoursData[0];
      values.Tue = openHoursData[1];
      values.Wed = openHoursData[2];
      values.Thu = openHoursData[3];
      values.Fri = openHoursData[4];
      values.Sat = openHoursData[5];
      values.Sun = openHoursData[6];
      let editCafeData = {
        body: values,
        cafeId: currentCafeId,
      };
      await editCafe(editCafeData);
    },
  });

  if (isLoading) return <Loading />;

  if (isSuccess) {
    toast.success('Cafe update successfully!', { toastId: 'success' });
    return <Navigate to={generatePath('/cafe/:id', { id: editData.data._id })} replace />;
  }

  return (
    <EditContainer elevation={3}>
      <CafeCreateForm onSubmit={formik.handleSubmit}>
        <ImageUpload formik={formik} />
        <ContentEditContainer>
          {mobileMatch ? (
            <>
              <CafeInfo formik={formik} />
              <OpenHours data={openHoursData} formik={formik} />
              <TimeZoneCreateBtn formik={formik} />
            </>
          ) : (
            <>
              <TimeZoneCreateBtn formik={formik} />
              <CafeInfo formik={formik} />
              <OpenHours data={openHoursData} formik={formik} />
            </>
          )}
        </ContentEditContainer>
      </CafeCreateForm>
    </EditContainer>
  );
};

export default CafeEditPage;
